import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { BaseComponent } from '../base/base-component.component';
import type { DropdownItemComponent } from '../dropdown-item/dropdown-item.component';
import { HelperService } from '../services/helper.service';
import { ResizedEvent } from '../directives/resize.directive';

@Component({
  standalone: false,
  selector: 'daui-dropdown',
  templateUrl: './dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./dropdown.component.less'],
})
export class DropdownComponent extends BaseComponent implements OnChanges {
  @Input() maxListHeight: number = 25.2;
  @Input() placeholder: string;

  @Input() set selectedValue(selectedValue: string | null | undefined) {
    if (this.helperService.isNullOrEmpty(selectedValue)) {
      this.resetSelection();
    } else {
      this.currentSelectedItem = selectedValue;
      this.updateSelectedItem();
    }
  }
  @Input() searchPlaceholder: string;
  @Input() hasSearch = false;
  @Input() disabled = false;

  @Output() selectionChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() searchTextChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() selectedValueChange: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('content', { static: false })
  content: ElementRef;
  @ViewChild('searchInput', { static: false })
  searchInput: ElementRef;

  textSearch = '';
  offestSearch = 12;

  currentHeight: number;
  controlHeight: number;
  currentSelectedContentItem = '';
  items: DropdownItemComponent[] = [];
  isDestroy: boolean;

  currentSelectedItem: string | null;
  isPopupOpen = false;
  isClearActivate = false;

  @HostListener('document:click', ['$event.target'])
  public onClick(targetElement: EventTarget | null) {
    if (this.elementRef) {
      if (this.isClearActivate) {
        this.isClearActivate = false;
        return;
      }
      const clickedInside =
        targetElement instanceof Node && this.elementRef.nativeElement.contains(targetElement);
      if (!clickedInside) {
        this.closePopup();
      }
    }
  }
  constructor(
    private helperService: HelperService,
    private cdRef: ChangeDetectorRef,
    private elementRef: ElementRef,
  ) {
    super();
  }

  protected onInit(): void {}

  protected onDestroy(): void {
    this.isDestroy = true;
  }

  subscribeItem(element: DropdownItemComponent) {
    this.items.push(element);

    setTimeout(() => {
      element.isActive = element.value === this.currentSelectedItem;

      if (element.isActive) {
        this.currentSelectedContentItem = element.content.nativeElement.innerHTML;
      }
    }, 0);
  }
  unsubscribeItem(element: DropdownItemComponent) {
    this.items = this.items.filter((item) => item !== element);
  }
  select(element: DropdownItemComponent) {
    const currentIsActive = element.isActive;

    if (element) {
      if (!currentIsActive) {
        this.currentSelectedItem = element.value;
        this.updateSelectedItem();
        this.selectionChanged(element.value);
      }
    }

    this.switchPopup();
  }

  resetSelection() {
    this.items.forEach((item) => {
      item.isActive = false;
    });
    this.currentSelectedItem = null;
  }

  selectionChanged(item: string) {
    if (!this.isDestroy) {
      this.selectionChange.emit(item);
      this.selectedValueChange.emit(item);
    }
  }

  updateSelectedItem() {
    if (this.items.length > 0 && !this.helperService.isNullOrEmpty(this.currentSelectedItem)) {
      setTimeout(() => {
        let isFindActive = false;
        this.items.forEach((element) => {
          element.isActive = element.value === this.currentSelectedItem;
          if (element.isActive) {
            this.currentSelectedContentItem = element.content.nativeElement.innerHTML;
            isFindActive = true;
          }
        });
        if (!isFindActive) {
          this.resetSelection();
        }
      }, 0);
    }
  }
  switchPopup() {
    if (this.disabled) {
      return;
    }

    this.searchEmit('');

    this.isPopupOpen = !this.isPopupOpen;
  }
  closePopup() {
    if (this.isPopupOpen) {
      this.isPopupOpen = false;
    }
  }
  onResize(_event: ResizedEvent) {
    this.updateMaxListHeight();
  }
  updateMaxListHeight() {
    if (this.content) {
      let currentHeightLocal: number = 0;
      const maxListHeightPxLocal: number = this.convertRemToPx(Number(this.maxListHeight));
      const contentHeightPxLocal = this.content.nativeElement.clientHeight;

      const heightWithSearch = this.adjustHeightWithSearch(contentHeightPxLocal);
      if (heightWithSearch > maxListHeightPxLocal) {
        // Superior maxListHeight / set to max
        currentHeightLocal = this.hasSearch
          ? maxListHeightPxLocal - this.offestSearch
          : maxListHeightPxLocal;
      } else {
        // Superior maxListHeight / adjust to size content
        currentHeightLocal = heightWithSearch;
      }
      this.currentHeight = 0;
      this.currentHeight = this.convertPxToRem(currentHeightLocal);
      // Force evaluate binding
      this.cdRef.detectChanges();
    }
  }
  adjustHeightWithSearch(value: number): number {
    return value + (this.searchInput ? this.searchInput.nativeElement.clientHeight : 0);
  }
  onSearch(searchText: string) {
    this.searchEmit(searchText);
  }
  searchEmit(searchText: string) {
    this.searchTextChange.emit(searchText);
  }
  onClear() {
    this.isClearActivate = true;
  }
  ngOnChanges(changes: SimpleChanges): void {
    // tslint:disable-next-line: forin
    for (const propName in changes) {
      switch (propName) {
        case 'maxListHeight':
          this.updateMaxListHeight();
          break;
      }
    }
  }
  convertRemToPx(value: number): number {
    return value * 10;
  }
  convertPxToRem(value: number): number {
    return value / 10;
  }
}
